import type { ReactElement } from 'react';
import type { BoxFileWithThumbnail } from 'src/components/advisor-dashboard/marketing/helpers';
import type { NextPageWithLayout } from 'src/pages/_app';
import { useMemo } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Button } from '@savvy-kit/components/button';
import PageContainer from '@savvy-kit/templates/page-container';
import PageTitle from '@savvy-kit/templates/page-title';
import AdvisorDashTopNavLayout from 'src/components/advisor-dashboard/layout/advisor-dash-top-nav-layout';
import {
  MarketingBrowseAssets,
  MarketingSearch,
} from 'src/components/advisor-dashboard/marketing/marketing-browse-assets';
import { MarketingEmailHubCard } from 'src/components/advisor-dashboard/marketing/marketing-email-hub-card';
import { MarketingOverviewAssetRow } from 'src/components/advisor-dashboard/marketing/marketing-overview-asset-row';
import { MarketingRequestList } from 'src/components/advisor-dashboard/marketing/marketing-requests-list';
import MarketingResourceList from 'src/components/advisor-dashboard/marketing/marketing-resource-list';
import { MarketingSwagStoreCard } from 'src/components/advisor-dashboard/marketing/marketing-swag-store-card';
import { TrackedButton } from 'src/components/tracked';
import { graphql } from 'src/gql';
import { withAdvisorGuard } from 'src/hocs/with-advisor-guard';

import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';
import { useTrackPageView } from '@savvy/deprecated-shared/tracking/use-track-page-view';
import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

export const MARKETING_QUERY = graphql(/* GraphQL */ `
  query Marketing($userId: ID!) {
    boxMarketingConfiguration(userId: $userId) {
      featuredFiles {
        files {
          id
          createdAt
          tags
          name
        }
      }
      resourcesFiles {
        files {
          id
          name
          tags
          parentPath
        }
      }
      emailTemplatesFiles {
        files {
          id
          name
          tags
          parentPath
        }
      }
      socialMediaFiles {
        files {
          id
          name
          tags
          parentPath
        }
      }
      brandMaterialFiles {
        files {
          id
          name
          tags
          parentPath
        }
      }
      advisorFiles {
        files {
          id
          createdAt
          tags
          name
        }
      }
    }
  }
`);

export const THUMBNAIL_QUERY = graphql(/* GraphQL */ `
  query BoxThumbnails($boxFileIds: [ID!]!) {
    boxThumbnails(boxFileIds: $boxFileIds) {
      fileId
      thumbnailData
    }
  }
`);

export const WRIKE_MARKETING_REQUEST_QUERY = graphql(/* GraphQL */ `
  query WrikeMarketingRequests {
    wrikeMarketingRequests {
      ...WrikeMarketingRequestList
    }
  }
`);

function Marketing() {
  useTrackPageView();
  const { user: authUser } = useAuth();

  const {
    data: wrikeMarketingRequestData,
    loading: wrikeMarketingRequestLoading,
    error: wrikeMarketingRequestError,
    refetch: refetchWrikeMarketingRequests,
  } = useQueryWithErrorLogging(WRIKE_MARKETING_REQUEST_QUERY);

  const { data: gqlData, loading: loadingAssetData } = useQueryWithErrorLogging(MARKETING_QUERY, {
    variables: {
      userId: authUser?.id || '',
    },
  });

  const allSearchableFiles = useMemo(() => {
    if (!gqlData?.boxMarketingConfiguration) return [];
    const config = gqlData.boxMarketingConfiguration;
    return [
      ...(config.resourcesFiles?.files ?? []).map((f) => ({ ...f, category: 'Resources' })),
      ...(config.emailTemplatesFiles?.files ?? []).map((f) => ({ ...f, category: 'Email templates' })),
      ...(config.socialMediaFiles?.files ?? []).map((f) => ({ ...f, category: 'Social media' })),
      ...(config.brandMaterialFiles?.files ?? []).map((f) => ({ ...f, category: 'Brand materials' })),
      ...(config.advisorFiles?.files ?? []).map((f) => ({ ...f, parentPath: null, category: 'Custom assets' })),
    ];
  }, [gqlData]);

  const assetCounts = useMemo(() => {
    if (!gqlData?.boxMarketingConfiguration) return undefined;
    const config = gqlData.boxMarketingConfiguration;
    return {
      resources: config.resourcesFiles?.files?.length ?? 0,
      emailTemplates: config.emailTemplatesFiles?.files?.length ?? 0,
      socialMedia: config.socialMediaFiles?.files?.length ?? 0,
      brandMaterials: config.brandMaterialFiles?.files?.length ?? 0,
    };
  }, [gqlData]);

  const advisorFiles = gqlData?.boxMarketingConfiguration.advisorFiles?.files ?? [];
  const advisorFileIds = advisorFiles.map((f) => f.id.toString());

  // For featuredFileIds
  const featuredFilesCopy = [...(gqlData?.boxMarketingConfiguration.featuredFiles?.files || [])];
  const featuredFileIds = featuredFilesCopy
    .sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return dateB - dateA;
    })
    .slice(0, 4)
    .map((entry) => entry.id.toString());

  const allThumbnailIds = [...new Set([...featuredFileIds, ...advisorFileIds])];

  const { data: thumbnailData, loading: loadingThumbnails } = useQueryWithErrorLogging(THUMBNAIL_QUERY, {
    variables: {
      boxFileIds: allThumbnailIds,
    },
    skip: !gqlData || allThumbnailIds.length === 0,
  });

  const featuredFiles = useMemo((): BoxFileWithThumbnail[] => {
    if (!gqlData?.boxMarketingConfiguration.featuredFiles?.files || !thumbnailData) return [];
    return gqlData.boxMarketingConfiguration.featuredFiles.files.reduce<BoxFileWithThumbnail[]>((acc, fileInfo) => {
      const thumbnail = thumbnailData.boxThumbnails.find((thumb) => thumb.fileId === fileInfo.id);
      const rawBase64: unknown = thumbnail?.thumbnailData;
      const base64 = typeof rawBase64 === 'string' ? rawBase64 : null;
      if (base64) acc.push({ boxFile: fileInfo, thumbnailBase64: base64 });
      return acc;
    }, []);
  }, [gqlData, thumbnailData]);

  const customAssetFiles = useMemo((): BoxFileWithThumbnail[] => {
    if (!advisorFiles.length || !thumbnailData) return [];
    return advisorFiles.reduce<BoxFileWithThumbnail[]>((acc, fileInfo) => {
      const thumbnail = thumbnailData.boxThumbnails.find((thumb) => thumb.fileId === fileInfo.id);
      const rawBase64: unknown = thumbnail?.thumbnailData;
      const base64 = typeof rawBase64 === 'string' ? rawBase64 : null;
      if (base64) acc.push({ boxFile: fileInfo, thumbnailBase64: base64 });
      return acc;
    }, []);
  }, [advisorFiles, thumbnailData]);

  const handleCreateRequest = () => {
    window.open('https://www.wrike.com/workspace.htm#/forms?formid=2460749', '_blank');
  };

  return (
    <>
      <PageTitle pageTitle="Marketing Center" breadcrumbs={[{ label: 'Marketing Center' }]} />

      {/* Hero band — full-width, edge-to-edge */}
      <div className="relative overflow-hidden border-b border-light">
        {/* Warm gradient texture background */}
        <div
          className="absolute inset-0 bg-gradient-to-b from-white to-stone-100 dark:from-card dark:to-muted"
          style={{ zIndex: 0 }}
        />
        <div
          className="absolute inset-0 mix-blend-soft-light opacity-45 dark:opacity-15"
          style={{
            backgroundImage: "url('/marketing/hero-texture.png')",
            backgroundSize: 'cover',
            zIndex: 1,
          }}
        />
        {/* Circle motif overlay */}
        <Image
          src="/marketing/hero-circles.svg"
          alt=""
          width={3378}
          height={1538}
          className="absolute left-1/2 -translate-x-1/2 pointer-events-none dark:opacity-30"
          style={{ width: '100%', height: 'auto', maxWidth: 1600, zIndex: 2, bottom: '-140%' }}
        />
        <div className="relative flex justify-center px-8 py-8" style={{ zIndex: 3 }}>
          <div className="max-w-[1400px] w-full">
            <div className="max-w-3xl mb-6">
              <p className="text-xl text-foreground tracking-tight leading-snug">
                Find the best assets to help market your practice –{' '}
                <span className="text-secondary">or request something new.</span>
              </p>
            </div>
            <div className="max-w-3xl">
              <div className="flex items-stretch gap-3">
                <div className="flex-1 min-w-0">
                  <MarketingSearch searchableFiles={allSearchableFiles} />
                </div>
                <span className="flex items-center text-sm text-secondary">or</span>
                <TrackedButton
                  elementName="submit-marketing-request-button"
                  variant="secondary"
                  onClick={handleCreateRequest}
                  className="shrink-0"
                >
                  Submit a request
                </TrackedButton>
              </div>
              <div className="mt-3 flex items-center gap-2 text-xs text-secondary">
                <span className="font-medium">Popular:</span>
                {['Retirement', 'Tax planning', '401k', 'Financial planning'].map((t) => (
                  <Link
                    key={t}
                    href={`/advisor/dashboard/marketing/discovery?search=${encodeURIComponent(t)}`}
                    className="inline-flex items-center h-7 px-3 rounded-full bg-card/80 border border-light hover:border-border hover:bg-card transition-colors"
                  >
                    {t}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <PageContainer>
        {/* Main content — two columns */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_388px] gap-8 pb-8">
          {/* Left column: Browse + Featured */}
          <div className="min-w-0 space-y-8">
            <section>
              <div className="flex items-baseline justify-between mb-4">
                <h2 className="text-lg font-semibold tracking-tight">Browse assets</h2>
                <Button variant="ghost" size="sm" render={<Link href="/advisor/dashboard/marketing/discovery" />}>
                  Open library &rarr;
                </Button>
              </div>
              <MarketingBrowseAssets counts={assetCounts} />
            </section>

            <section>
              <MarketingEmailHubCard />
            </section>

            <section>
              <div className="bg-stone-50 dark:bg-card rounded-md px-4 py-6">
                <MarketingOverviewAssetRow
                  title="Featured"
                  emptyText="No featured assets. Check back in soon!"
                  loadingAssets={loadingAssetData || loadingThumbnails}
                  boxFilesWithThumbnails={featuredFiles}
                />
              </div>
            </section>

            <section>
              <MarketingOverviewAssetRow
                title="Your custom assets"
                emptyText="No custom assets yet"
                loadingAssets={loadingAssetData || loadingThumbnails}
                boxFilesWithThumbnails={customAssetFiles.slice(0, 3)}
                headerAction={
                  customAssetFiles.length > 0 ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      render={<Link href="/advisor/dashboard/marketing/discovery?assetType=myCustomAssets" />}
                    >
                      View all &rarr;
                    </Button>
                  ) : undefined
                }
              />
            </section>
          </div>

          {/* Right column: Requests + FAQ + Swag */}
          <aside className="space-y-6">
            <MarketingRequestList
              wrikeQueryFrag={wrikeMarketingRequestData?.wrikeMarketingRequests}
              loading={wrikeMarketingRequestLoading}
              error={wrikeMarketingRequestError}
              refetchWrikeMarketingRequests={refetchWrikeMarketingRequests}
            />
            <MarketingResourceList />
            <MarketingSwagStoreCard />
          </aside>
        </div>
      </PageContainer>
    </>
  );
}

const MarketingPage: NextPageWithLayout = withAdvisorGuard(Marketing);
const WrappedMarketingPage: NextPageWithLayout = MarketingPage;
WrappedMarketingPage.getLayout = function getLayout(page: ReactElement) {
  return <AdvisorDashTopNavLayout>{page}</AdvisorDashTopNavLayout>;
};

export default WrappedMarketingPage;
